/* ProMechBC product lightbox — behaviour for snippets/product-media-modal.liquid.
   Styles: pmb-lightbox.css.

   One photo at a time on a studio canvas. The opener's photo morphs into
   place (FLIP), slides follow the finger or mouse, and zoom works by click,
   double-tap, pinch, wheel or keyboard. Swipe down to dismiss.

   Keeps the ModalDialog contract (show(opener) / hide(), #ModalClose-*,
   focus trap, body scroll lock), and survives product-info.js swapping the
   slides on variant change. */

if (!customElements.get('product-modal')) {
  const pad = (n) => String(n).padStart(2, '0');
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

  // Visible box of an <img> drawn with object-fit: contain.
  const containRect = (img) => {
    const r = img.getBoundingClientRect();
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    const fit = getComputedStyle(img).objectFit;
    if (!nw || !nh || !r.width || !r.height || (fit !== 'contain' && fit !== 'scale-down')) return r;
    let w = r.width;
    let h = r.height;
    if (nw / nh > w / h) h = w / (nw / nh);
    else w = h * (nw / nh);
    const left = r.left + (r.width - w) / 2;
    const top = r.top + (r.height - h) / 2;
    return { left, top, width: w, height: h, right: left + w, bottom: top + h };
  };

  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.dialog = this.querySelector('[role="dialog"]');
        this.stage = this.querySelector('[data-lb-stage]');
        this.track = this.querySelector('.pmb-lb__track');
        if (!this.stage || !this.track) return;

        this.strip = this.querySelector('[data-lb-strip]');
        this.countNow = this.querySelector('.pmb-lb__count-now');
        this.countAll = this.querySelector('[data-lb-total]');
        this.live = this.querySelector('[data-lb-live]');
        this.zoomBtn = this.querySelector('[data-lb-zoom]');
        this.prevBtn = this.querySelector('[data-lb-prev]');
        this.nextBtn = this.querySelector('[data-lb-next]');
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.slides = [];
        this.index = 0;
        this.z = { s: 1, x: 0, y: 0 };
        this.pointers = new Map();
        this.g = null;

        // ModalDialog closes media modals on any mouse pointerup; the stage
        // needs clicks and drags for itself, so closing is decided here.
        this.dialog.addEventListener('pointerup', (e) => e.stopPropagation());
        // Esc while zoomed only zooms out (ModalDialog closes on keyup).
        this.dialog.addEventListener('keyup', (e) => {
          if (e.code === 'Escape' && this.eatEsc) {
            this.eatEsc = false;
            e.stopPropagation();
          }
        });
        this.addEventListener('keydown', this.onKey.bind(this));

        this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
        this.nextBtn.addEventListener('click', () => this.go(this.index + 1));
        this.zoomBtn.addEventListener('click', () => this.toggleZoom());

        this.stage.addEventListener('pointerdown', this.onDown.bind(this));
        this.stage.addEventListener('pointermove', this.onMove.bind(this));
        this.stage.addEventListener('pointerup', this.onUp.bind(this));
        this.stage.addEventListener('pointercancel', this.onUp.bind(this));
        this.stage.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
        this.addEventListener('pointermove', this.onIdleWake.bind(this));

        this.onResize = () => {
          this.resetZoom(false);
          this.setTrack(0);
        };

        new MutationObserver(() => this.indexSlides()).observe(this.track, { childList: true });
        this.indexSlides();
      }

      /* ---- Slides ------------------------------------------------------ */

      indexSlides() {
        const keep = this.slides[this.index]?.dataset.mediaId;
        this.slides = Array.from(this.track.querySelectorAll(':scope > .pmb-lb__slide'));
        const n = this.slides.length;

        this.slides.forEach((slide, i) => {
          slide.style.setProperty('--n', i);
          slide.setAttribute('aria-label', `${i + 1} of ${n}`);
          const img = slide.querySelector('.pmb-lb__img');
          if (!img || (img.complete && img.naturalWidth)) {
            slide.classList.add('is-loaded');
          } else {
            const loaded = () => slide.classList.add('is-loaded');
            img.addEventListener('load', loaded, { once: true });
            img.addEventListener('error', loaded, { once: true });
          }
        });

        this.countAll.textContent = pad(n);
        this.classList.toggle('is-single', n < 2);
        this.buildStrip();

        const i = this.slides.findIndex((s) => s.dataset.mediaId === keep);
        this.go(i < 0 ? 0 : i, false, true);
      }

      buildStrip() {
        this.strip.textContent = '';
        if (this.slides.length < 2) return;
        this.thumbs = this.slides.map((slide, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'pmb-lb__thumb';
          b.setAttribute('aria-label', `View image ${i + 1} of ${this.slides.length}`);
          const img = document.createElement('img');
          img.src = slide.dataset.thumb;
          img.alt = '';
          img.loading = 'lazy';
          img.decoding = 'async';
          b.appendChild(img);
          if (!slide.classList.contains('pmb-lb__slide--image')) {
            b.insertAdjacentHTML(
              'beforeend',
              '<span class="pmb-lb__thumb-play" aria-hidden="true"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M8 5.5v13l10.5-6.5z"/></svg></span>'
            );
          }
          b.addEventListener('click', () => this.go(i));
          this.strip.appendChild(b);
          return b;
        });
      }

      activeSlide() {
        return this.slides[this.index];
      }

      activeImg() {
        return this.activeSlide()?.querySelector('.pmb-lb__img') || null;
      }

      setTrack(dx) {
        this.track.style.left = `calc(${-this.index} * (100% + var(--lb-gap)) + ${dx}px)`;
      }

      go(i, animate = true, silent = false) {
        const n = this.slides.length;
        if (!n) return;
        i = clamp(i, 0, n - 1);
        const prev = this.index;
        const moved = i !== prev;

        if (moved) {
          this.resetZoom(false);
          this.dropGhost();
          window.pauseAllMedia?.();
        }
        this.index = i;
        this.track.classList.toggle('is-animating', animate && !this.reduced.matches);
        this.setTrack(0);

        this.slides.forEach((slide, k) => {
          const on = k === i;
          slide.classList.toggle('is-active', on);
          slide.inert = !on;
          // Warm the neighbours so the next photo is already there.
          if (Math.abs(k - i) <= 1) {
            const img = slide.querySelector('.pmb-lb__img');
            if (img && img.loading === 'lazy') img.loading = 'eager';
          }
        });

        // Counter: the new number rolls in from the direction of travel.
        const num = document.createElement('span');
        num.textContent = pad(i + 1);
        if (moved && animate) num.className = i > prev ? 'is-up' : 'is-down';
        this.countNow.replaceChildren(num);
        this.style.setProperty('--lb-progress', ((i + 1) / n).toFixed(4));

        this.prevBtn.disabled = i === 0;
        this.nextBtn.disabled = i === n - 1;
        this.zoomBtn.disabled = !this.activeImg();

        if (this.thumbs) {
          this.thumbs.forEach((t, k) => t.setAttribute('aria-current', k === i ? 'true' : 'false'));
          const t = this.thumbs[i];
          if (t) {
            this.strip.scrollTo({
              left: t.offsetLeft - (this.strip.clientWidth - t.offsetWidth) / 2,
              behavior: animate ? 'smooth' : 'auto',
            });
          }
        }

        if (moved && !silent) {
          this.live.textContent = `Image ${i + 1} of ${n}`;
          this.finishHint();
        }
      }

      /* ---- Open / close ------------------------------------------------ */

      show(opener) {
        if (!this.stage) return super.show(opener);

        clearTimeout(this.closeTimer);
        this.closing = false;
        this.classList.remove('is-closing', 'is-ready', 'is-immersive', 'is-idle', 'is-zoomed');

        this.openedId = opener?.getAttribute?.('data-media-id') || null;
        this.sourceImg = opener?.closest?.('modal-opener')?.querySelector('img') || null;
        const i = this.slides.findIndex((s) => s.dataset.mediaId === this.openedId);
        this.index = i < 0 ? 0 : i;
        this.resetZoom(false);
        this.go(this.index, false, true);

        super.show(opener);
        this.flipIn();
        window.addEventListener('resize', this.onResize);

        requestAnimationFrame(() => {
          this.readyTimer = setTimeout(() => this.classList.add('is-ready'), this.reduced.matches ? 0 : 140);
        });
        if (this.hinted) this.classList.add('is-hint-done');
        else this.hintTimer = setTimeout(() => this.finishHint(), 5200);
      }

      hide(opts) {
        if (!this.stage) return super.hide();
        if (!this.hasAttribute('open') || this.closing) return;
        this.closing = true;
        clearTimeout(this.readyTimer);
        clearTimeout(this.idleTimer);
        clearTimeout(this.tapTimer);
        this.dropGhost();
        const drag = opts && typeof opts === 'object' ? opts : null;

        const slide = this.activeSlide();
        const id = slide?.dataset.mediaId;
        const same = id && id === this.openedId;
        // Leave the page gallery on the photo the customer ended on.
        if (id && !same) {
          document
            .getElementById(`MediaGallery-${this.dataset.section}`)
            ?.setActiveMedia?.(`${this.dataset.section}-${id}`, false);
        }

        const img = this.activeImg();
        const media = img || slide?.querySelector('.pmb-lb__frame');
        if (media && !this.reduced.matches) {
          const from = getComputedStyle(media).transform;
          let to = null;
          let fade = true;

          // Same photo as opened and the page photo is on screen: fly home.
          if (img && same && !drag && this.z.s <= 1.01 && this.sourceImg) {
            const t = containRect(this.sourceImg);
            const b = img.parentElement.getBoundingClientRect();
            if (t.width && b.width && t.bottom > 0 && t.top < window.innerHeight) {
              const dx = t.left + t.width / 2 - (b.left + b.width / 2);
              const dy = t.top + t.height / 2 - (b.top + b.height / 2);
              to = `translate3d(${dx}px, ${dy}px, 0) scale(${t.width / b.width})`;
              fade = false;
            }
          }
          if (!to && drag) {
            const away = drag.dy + Math.sign(drag.dy || 1) * window.innerHeight * 0.3;
            to = `translate3d(${drag.dx * 0.3}px, ${away}px, 0) scale(0.82)`;
          }
          if (!to) to = `${from === 'none' ? '' : from} scale(0.96)`;

          media.classList.remove('is-settling');
          this.closeAnim = media.animate(
            [{ transform: from }, fade ? { transform: to, opacity: 0 } : { transform: to }],
            { duration: 420, easing: EASE, fill: 'forwards' }
          );
        }

        this.classList.add('is-closing');
        const done = () => {
          this.closeAnim?.cancel();
          this.closeAnim = null;
          this.resetZoom(false);
          if (media && media !== img) media.style.transform = '';
          this.classList.remove('is-closing', 'is-ready', 'is-immersive', 'is-idle', 'is-dragging-y', 'is-panning');
          this.style.removeProperty('--lb-dim');
          window.removeEventListener('resize', this.onResize);
          this.closing = false;
          super.hide();
        };
        if (this.reduced.matches) done();
        else this.closeTimer = setTimeout(done, 400);
      }

      // The page photo lifts off and lands in the viewer. A ghost copy flies
      // (the full-size photo may still be downloading) and hands over once
      // the real one is ready.
      flipIn() {
        const img = this.activeImg();
        const src = this.sourceImg;
        if (!img || !src || !src.currentSrc || this.reduced.matches) return;
        const from = containRect(src);
        const to = img.parentElement.getBoundingClientRect();
        if (!from.width || !to.width || from.bottom < 0 || from.top > window.innerHeight) return;

        const ghost = document.createElement('img');
        ghost.className = 'pmb-lb__ghost';
        ghost.alt = '';
        ghost.src = src.currentSrc;
        Object.assign(ghost.style, {
          left: `${to.left}px`,
          top: `${to.top}px`,
          width: `${to.width}px`,
          height: `${to.height}px`,
        });
        this.dialog.insertBefore(ghost, this.stage.nextSibling);
        this.ghost = ghost;
        img.style.visibility = 'hidden';

        const sx = from.width / to.width;
        const sy = from.height / to.height;
        const flight = ghost.animate(
          [
            { transform: `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${sx}, ${sy})` },
            { transform: 'none' },
          ],
          { duration: 620, easing: EASE }
        );

        const slide = this.activeSlide();
        flight.onfinish = () => {
          if (this.ghost !== ghost) return;
          img.style.visibility = '';
          if (slide.classList.contains('is-loaded')) {
            this.dropGhost();
          } else {
            const handOver = () => setTimeout(() => this.ghost === ghost && this.dropGhost(), 650);
            img.addEventListener('load', handOver, { once: true });
            img.addEventListener('error', handOver, { once: true });
          }
        };
      }

      dropGhost() {
        if (!this.ghost) return;
        this.ghost.remove();
        this.ghost = null;
        this.slides.forEach((s) => {
          const img = s.querySelector('.pmb-lb__img');
          if (img) img.style.visibility = '';
        });
      }

      /* ---- Zoom -------------------------------------------------------- */

      maxScale(img) {
        const natural = Number(img.dataset.naturalWidth) || img.naturalWidth || 0;
        const shown = img.offsetWidth || 1;
        return clamp((natural / shown) * 1.15, 2, 4);
      }

      zoomTarget(img) {
        return Math.min(this.maxScale(img), 2.5);
      }

      // Zoom so the point (px, py) stays under the finger / cursor.
      zoomAt(s, px, py, animate) {
        const img = this.activeImg();
        if (!img) return;
        const b = img.parentElement.getBoundingClientRect();
        const cx = b.left + b.width / 2;
        const cy = b.top + b.height / 2;
        if (px === undefined) {
          px = cx + this.z.x;
          py = cy + this.z.y;
        }
        const k = s / this.z.s;
        this.z = {
          s,
          x: px - cx - (px - cx - this.z.x) * k,
          y: py - cy - (py - cy - this.z.y) * k,
        };
        this.clampPan();
        this.applyZoom(animate);

        // Ask the srcset for a sharper file once we're magnified.
        const want = Math.ceil(b.width * s);
        if (s > 1.01 && want > (img.lbSizes || 0)) {
          img.lbSizes = want;
          img.sizes = `${want}px`;
        }
      }

      bounds(s) {
        const img = this.activeImg();
        const b = img.parentElement.getBoundingClientRect();
        const W = window.innerWidth;
        const H = window.innerHeight;
        const w = b.width * s;
        const h = b.height * s;
        const cx = b.left + b.width / 2;
        const cy = b.top + b.height / 2;
        return {
          minX: w > W ? W - w / 2 - cx : 0,
          maxX: w > W ? w / 2 - cx : 0,
          minY: h > H ? H - h / 2 - cy : 0,
          maxY: h > H ? h / 2 - cy : 0,
        };
      }

      clampPan(soft = false) {
        if (!this.activeImg()) return;
        const r = this.bounds(this.z.s);
        const fit = (v, lo, hi) => {
          const c = clamp(v, lo, hi);
          return soft ? c + (v - c) * 0.3 : c;
        };
        this.z.x = fit(this.z.x, r.minX, r.maxX);
        this.z.y = fit(this.z.y, r.minY, r.maxY);
      }

      applyZoom(animate) {
        const img = this.activeImg();
        if (img) {
          img.classList.toggle('is-settling', !!animate && !this.reduced.matches);
          const { s, x, y } = this.z;
          img.style.transform = s === 1 && !x && !y ? '' : `translate3d(${x}px, ${y}px, 0) scale(${s})`;
        }
        const zoomed = this.z.s > 1.01;
        this.classList.toggle('is-zoomed', zoomed);
        this.zoomBtn.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
        this.zoomBtn.setAttribute('aria-label', zoomed ? 'Zoom out' : 'Zoom in');
        if (zoomed) this.finishHint();
      }

      resetZoom(animate) {
        this.z = { s: 1, x: 0, y: 0 };
        this.applyZoom(animate);
      }

      toggleZoom(px, py) {
        const img = this.activeImg();
        if (!img) return;
        if (this.z.s > 1.01) this.resetZoom(true);
        else this.zoomAt(this.zoomTarget(img), px, py, true);
      }

      /* ---- Gestures ---------------------------------------------------- */

      onDown(e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('.pmb-lb__nav, model-viewer, video, iframe, deferred-media[loaded], product-model[loaded]')) return;

        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (this.pointers.size === 1) {
          this.g = {
            mode: null,
            id: e.pointerId,
            type: e.pointerType,
            target: e.target,
            sx: e.clientX,
            sy: e.clientY,
            x0: this.z.x,
            y0: this.z.y,
            hist: [{ x: e.clientX, y: e.clientY, t: e.timeStamp }],
          };
        } else if (this.pointers.size === 2 && this.activeImg()) {
          const [a, b] = Array.from(this.pointers.values());
          if (this.g?.mode === 'swipe') this.go(this.index);
          this.classList.remove('is-dragging-y');
          this.style.removeProperty('--lb-dim');
          this.g = {
            mode: 'pinch',
            d0: Math.hypot(b.x - a.x, b.y - a.y) || 1,
            m0: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
            s0: this.z.s,
            x0: this.z.x,
            y0: this.z.y,
          };
          this.pointers.forEach((_, id) => this.capture(id));
        }
      }

      capture(id) {
        try {
          this.stage.setPointerCapture(id);
        } catch (err) {
          /* pointer already gone */
        }
      }

      onMove(e) {
        const p = this.pointers.get(e.pointerId);
        const g = this.g;
        if (!p || !g) return;
        p.x = e.clientX;
        p.y = e.clientY;

        if (g.mode === 'pinch') {
          if (this.pointers.size < 2) return;
          const [a, b] = Array.from(this.pointers.values());
          const img = this.activeImg();
          const max = this.maxScale(img);
          let s = (g.s0 * Math.hypot(b.x - a.x, b.y - a.y)) / g.d0;
          // Rubber-band past the limits; settles back on release.
          if (s < 1) s = 1 - (1 - s) * 0.4;
          if (s > max) s = max + (s - max) * 0.3;
          const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
          const bx = img.parentElement.getBoundingClientRect();
          const cx = bx.left + bx.width / 2;
          const cy = bx.top + bx.height / 2;
          const k = s / g.s0;
          this.z = {
            s,
            x: m.x - cx - (g.m0.x - cx - g.x0) * k,
            y: m.y - cy - (g.m0.y - cy - g.y0) * k,
          };
          this.applyZoom(false);
          return;
        }

        if (e.pointerId !== g.id) return;
        const dx = e.clientX - g.sx;
        const dy = e.clientY - g.sy;

        if (!g.mode) {
          if (Math.hypot(dx, dy) < 6) return;
          if (this.z.s > 1.01) g.mode = 'pan';
          else if (Math.abs(dx) > Math.abs(dy)) g.mode = 'swipe';
          else g.mode = 'dismiss';
          this.capture(e.pointerId);
          this.track.classList.remove('is-animating');
          if (g.mode === 'pan') this.classList.add('is-panning');
          if (g.mode === 'dismiss') this.classList.add('is-dragging-y');
          clearTimeout(this.tapTimer);
        }

        g.hist.push({ x: e.clientX, y: e.clientY, t: e.timeStamp });
        while (g.hist.length > 2 && e.timeStamp - g.hist[0].t > 100) g.hist.shift();

        if (g.mode === 'pan') {
          this.z.x = g.x0 + dx;
          this.z.y = g.y0 + dy;
          this.clampPan(true);
          this.applyZoom(false);
        } else if (g.mode === 'swipe') {
          const atEdge = (this.index === 0 && dx > 0) || (this.index === this.slides.length - 1 && dx < 0);
          this.setTrack(atEdge ? dx * 0.32 : dx);
        } else if (g.mode === 'dismiss') {
          const media = this.activeImg() || this.activeSlide()?.querySelector('.pmb-lb__frame');
          const H = window.innerHeight;
          const f = Math.min(Math.abs(dy) / H, 1);
          if (media) media.style.transform = `translate3d(${dx * 0.3}px, ${dy}px, 0) scale(${1 - f * 0.2})`;
          this.style.setProperty('--lb-dim', (1 - Math.min(Math.abs(dy) / (H * 0.55), 1) * 0.9).toFixed(3));
        }
      }

      onUp(e) {
        if (!this.pointers.has(e.pointerId)) return;
        this.pointers.delete(e.pointerId);
        const g = this.g;
        if (!g) return;
        const cancelled = e.type === 'pointercancel';

        if (g.mode === 'pinch') {
          if (this.pointers.size === 1) {
            // One finger left down: carry on as a pan from here.
            const [[id, p]] = Array.from(this.pointers.entries());
            this.g = { mode: 'pan', id, sx: p.x, sy: p.y, x0: this.z.x, y0: this.z.y, hist: [{ x: p.x, y: p.y, t: e.timeStamp }] };
            this.settleZoom(p.x, p.y);
            this.g.x0 = this.z.x;
            this.g.y0 = this.z.y;
            return;
          }
          this.settleZoom(g.m0.x, g.m0.y);
          this.g = null;
          return;
        }
        if (this.pointers.size || e.pointerId !== g.id) return;
        this.g = null;

        const h = g.hist;
        const first = h[0];
        const last = h[h.length - 1];
        const dt = Math.max(last.t - first.t, 1);
        const vx = (last.x - first.x) / dt;
        const vy = (last.y - first.y) / dt;
        const dx = e.clientX - g.sx;
        const dy = e.clientY - g.sy;

        if (!g.mode) {
          if (!cancelled) this.onTap(e, g);
          return;
        }

        if (g.mode === 'pan') {
          this.classList.remove('is-panning');
          // A flick keeps travelling a little before the edges catch it.
          this.z.x += vx * 140;
          this.z.y += vy * 140;
          this.clampPan();
          this.applyZoom(true);
        } else if (g.mode === 'swipe') {
          const W = this.stage.clientWidth;
          if ((dx < -W * 0.16 || vx < -0.45) && this.index < this.slides.length - 1) this.go(this.index + 1);
          else if ((dx > W * 0.16 || vx > 0.45) && this.index > 0) this.go(this.index - 1);
          else this.go(this.index);
        } else if (g.mode === 'dismiss') {
          if (!cancelled && (Math.abs(dy) > 120 || Math.abs(vy) > 0.6)) {
            this.hide({ dx, dy });
          } else {
            const media = this.activeImg() || this.activeSlide()?.querySelector('.pmb-lb__frame');
            this.classList.remove('is-dragging-y');
            this.style.removeProperty('--lb-dim');
            if (media) {
              media.animate([{ transform: media.style.transform }, { transform: 'none' }], { duration: 420, easing: EASE });
              media.style.transform = '';
            }
          }
        }
      }

      settleZoom(px, py) {
        const img = this.activeImg();
        if (!img) return;
        const max = this.maxScale(img);
        if (this.z.s <= 1.02) this.resetZoom(true);
        else if (this.z.s > max) this.zoomAt(max, px, py, true);
        else {
          this.clampPan();
          this.applyZoom(true);
        }
      }

      // Mouse: click the photo to zoom, click the canvas to close.
      // Touch: double-tap to zoom, single tap hides the chrome.
      onTap(e, g) {
        if (g.target.closest('button')) return;
        const onPhoto = !!g.target.closest('.pmb-lb__img');

        if (g.type === 'mouse') {
          if (onPhoto) this.toggleZoom(e.clientX, e.clientY);
          else if (!g.target.closest('.pmb-lb__frame')) this.hide();
          return;
        }

        const last = this.lastTap;
        if (last && e.timeStamp - last.t < 300 && Math.hypot(e.clientX - last.x, e.clientY - last.y) < 30) {
          clearTimeout(this.tapTimer);
          this.lastTap = null;
          this.toggleZoom(e.clientX, e.clientY);
          return;
        }
        this.lastTap = { t: e.timeStamp, x: e.clientX, y: e.clientY };
        this.tapTimer = setTimeout(() => {
          this.lastTap = null;
          this.classList.toggle('is-immersive');
        }, 280);
      }

      onWheel(e) {
        const img = this.activeImg();
        if (!img) return;
        e.preventDefault();
        const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1;
        const dx = e.deltaX * unit;
        const dy = e.deltaY * unit;

        // Sideways scroll pans a zoomed photo, otherwise turns the page.
        if (!e.ctrlKey && Math.abs(dx) > Math.abs(dy)) {
          if (this.z.s > 1.01) {
            this.z.x -= dx;
            this.clampPan();
            this.applyZoom(false);
          } else if (Math.abs(dx) > 24 && !this.wheelLock) {
            this.wheelLock = true;
            setTimeout(() => (this.wheelLock = false), 550);
            this.go(this.index + (dx > 0 ? 1 : -1));
          }
          return;
        }

        const s = clamp(this.z.s * Math.exp(-dy * (e.ctrlKey ? 0.01 : 0.0025)), 1, this.maxScale(img));
        if (s === this.z.s) return;
        if (s <= 1.005) this.resetZoom(false);
        else this.zoomAt(s, e.clientX, e.clientY, false);
      }

      onKey(e) {
        if (!this.hasAttribute('open') || e.target.closest('input, textarea, model-viewer')) return;
        const zoomed = this.z.s > 1.01;
        switch (e.key) {
          case 'ArrowRight':
            this.go(this.index + 1);
            break;
          case 'ArrowLeft':
            this.go(this.index - 1);
            break;
          case 'Home':
            this.go(0);
            break;
          case 'End':
            this.go(this.slides.length - 1);
            break;
          case '+':
          case '=':
            if (this.activeImg()) this.zoomAt(Math.min(this.z.s * 1.5, this.maxScale(this.activeImg())), undefined, undefined, true);
            break;
          case '-':
          case '_':
            if (this.z.s / 1.5 <= 1.01) this.resetZoom(true);
            else this.zoomAt(this.z.s / 1.5, undefined, undefined, true);
            break;
          case '0':
            this.resetZoom(true);
            break;
          case 'Escape':
            if (!zoomed) return;
            this.eatEsc = true;
            this.resetZoom(true);
            break;
          default:
            return;
        }
        e.preventDefault();
      }

      /* ---- Chrome ------------------------------------------------------ */

      // With a mouse, the chrome steps aside after a few still seconds.
      onIdleWake(e) {
        if (e.pointerType !== 'mouse' || !this.hasAttribute('open')) return;
        this.classList.remove('is-idle');
        clearTimeout(this.idleTimer);
        if (e.target.closest('.pmb-lb__bar, .pmb-lb__foot, .pmb-lb__nav')) return;
        this.idleTimer = setTimeout(() => this.classList.add('is-idle'), 2800);
      }

      finishHint() {
        if (this.hinted) return;
        this.hinted = true;
        clearTimeout(this.hintTimer);
        this.classList.add('is-hint-done');
      }
    }
  );
}
